import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { Badge } from '../../components/ui/badge';
import { X, Plus, Loader2, Shield, Clock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth-context';
import { AppRemoteConfigCard } from '../../components/admin/AppRemoteConfigCard';
import { PrenatalWindowCleanupCard } from '../../components/admin/PrenatalWindowCleanupCard';

export default function SettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');

  useEffect(() => {
    api.get('/api/settings').then(res => setSettings(res.data)).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/settings', settings);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const addKeyword = () => {
    if (!newKeyword.trim()) return;
    setSettings(prev => ({
      ...prev,
      audit_keywords: [...(prev.audit_keywords || []), newKeyword.trim()]
    }));
    setNewKeyword('');
  };

  const removeKeyword = (idx) => {
    setSettings(prev => ({
      ...prev,
      audit_keywords: prev.audit_keywords.filter((_, i) => i !== idx)
    }));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!settings) return <div className="text-center py-12"><p className="text-muted-foreground">Unable to load settings</p></div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">LMS Settings</h1>
        <p className="text-muted-foreground mt-1">Configure global LMS panel settings</p>
      </div>

      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><Clock className="h-5 w-5" />Class Booking (Cult Model)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Advance Booking Window (days)</p><p className="text-xs text-muted-foreground">How many days ahead Elite members can book group classes</p></div>
            <Input type="number" className="w-20" value={settings?.advance_booking_days || 7} onChange={e => setSettings({...settings, advance_booking_days: parseInt(e.target.value)})} />
          </div>
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Default Max Capacity</p><p className="text-xs text-muted-foreground">Maximum learners per group session (overridable per batch)</p></div>
            <Input type="number" className="w-20" value={settings?.default_max_capacity || 15} onChange={e => setSettings({...settings, default_max_capacity: parseInt(e.target.value)})} />
          </div>
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Booking Cancel Window (hours)</p><p className="text-xs text-muted-foreground">How many hours before class a booking can be cancelled</p></div>
            <Input type="number" className="w-20" value={settings?.booking_cancel_hours || 12} onChange={e => setSettings({...settings, booking_cancel_hours: parseInt(e.target.value)})} />
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><Clock className="h-5 w-5" />Session Timing</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Instructor Start Buffer</p><p className="text-xs text-muted-foreground">Minutes before class start when instructor can start</p></div>
            <Input type="number" className="w-20" value={settings?.session_start_buffer_instructor || 10} onChange={e => setSettings({...settings, session_start_buffer_instructor: parseInt(e.target.value)})} />
          </div>
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Learner Join Buffer</p><p className="text-xs text-muted-foreground">Minutes before class start when learner can join</p></div>
            <Input type="number" className="w-20" value={settings?.session_start_buffer_learner || 5} onChange={e => setSettings({...settings, session_start_buffer_learner: parseInt(e.target.value)})} />
          </div>
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">No-Show Wait (minutes)</p><p className="text-xs text-muted-foreground">Time instructor waits before session counts as no-show</p></div>
            <Input type="number" className="w-20" value={settings?.no_show_wait_minutes || 15} onChange={e => setSettings({...settings, no_show_wait_minutes: parseInt(e.target.value)})} />
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><AlertTriangle className="h-5 w-5" />Cancellation</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Instructor Cancel Penalty (INR)</p></div>
            <Input type="number" className="w-24" value={settings?.instructor_cancel_penalty || 600} onChange={e => setSettings({...settings, instructor_cancel_penalty: parseFloat(e.target.value)})} />
          </div>
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Credit Loss Window (hours)</p><p className="text-xs text-muted-foreground">Cancellation within this window loses credit</p></div>
            <Input type="number" className="w-20" value={settings?.learner_cancel_credit_window_hours || 24} onChange={e => setSettings({...settings, learner_cancel_credit_window_hours: parseInt(e.target.value)})} />
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><Shield className="h-5 w-5" />Audit Keywords</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">Flag sessions where these keywords appear in transcript or AI summary</p>
          <div className="flex gap-2">
            <Input placeholder="Add keyword..." value={newKeyword} onChange={e => setNewKeyword(e.target.value)} onKeyDown={e => e.key === 'Enter' && addKeyword()} data-testid="audit-keyword-input" />
            <Button variant="outline" onClick={addKeyword} data-testid="add-keyword-button"><Plus className="h-4 w-4 mr-1" />Add</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(settings?.audit_keywords || []).map((kw, i) => (
              <Badge key={i} variant="secondary" className="flex items-center gap-1">
                {kw}<button onClick={() => removeKeyword(i)}><X className="h-3 w-3" /></button>
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="font-heading text-xl">Trial Videos</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">Upload URLs for trimester-specific trial videos shown to free users</p>
          {[1, 2, 3].map(t => (
            <div key={t}>
              <label className="text-sm font-medium mb-1.5 block">Trimester {t} Video URL</label>
              <Input placeholder="https://..." value={settings?.[`trial_video_trimester_${t}`] || ''} onChange={e => setSettings({...settings, [`trial_video_trimester_${t}`]: e.target.value})} />
            </div>
          ))}
        </CardContent>
      </Card>

      <AppRemoteConfigCard />

      {user?.role === 'superadmin' && <PrenatalWindowCleanupCard />}

      <Button onClick={save} disabled={saving} className="w-full" data-testid="save-settings-button">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save Settings
      </Button>
    </div>
  );
}
